import { supabase } from './supabaseService';
import { logger } from '../utils/logger';
import type { User, UserSession, UserPermissions } from '../types';
import { sessionIntegrityService } from './sessionIntegrityService';
import { versionManager } from '../utils/versionManager';
import { structuredLogger } from './structuredLogger';

class AuthService {
  private currentSession: UserSession | null = null;
  private authEventProcessing: boolean = false;
  private lastProcessedEventTimestamp: number = 0;
  private processedEventIds: Set<string> = new Set();
  // TTL cache. Entries older than PROFILE_CACHE_TTL_MS are treated as absent
  // so role changes made in the DB propagate within a few minutes without
  // requiring the user to log out. Se persiste en localStorage para que
  // page reloads con sesión activa no bloqueen esperando la consulta al
  // perfil (era el mayor factor de "tarda al cargar" tras el login).
  private static readonly PROFILE_CACHE_TTL_MS = 5 * 60 * 1000;
  private static readonly PROFILE_CACHE_KEY = 'crm_profile_cache_v1';
  private profileCache: Map<string, { profile: any; expiresAt: number }> = new Map();
  // login() runs handleAuthenticatedUser inline; Supabase then fires a SIGNED_IN
  // event that would run it again. This flag lets us swallow that echo.
  private suppressNextSignedIn: boolean = false;

  private persistProfileCache(): void {
    try {
      const entries: Record<string, { profile: any; expiresAt: number }> = {};
      const now = Date.now();
      this.profileCache.forEach((entry, uid) => {
        if (entry.expiresAt > now) entries[uid] = entry;
      });
      localStorage.setItem(AuthService.PROFILE_CACHE_KEY, JSON.stringify(entries));
    } catch {
      // localStorage lleno o inaccesible: ignoramos, degradamos a caché en memoria.
    }
  }

  private hydrateProfileCache(): void {
    try {
      const raw = localStorage.getItem(AuthService.PROFILE_CACHE_KEY);
      if (!raw) return;
      const entries = JSON.parse(raw) as Record<string, { profile: any; expiresAt: number }>;
      const now = Date.now();
      Object.entries(entries).forEach(([uid, entry]) => {
        if (entry.expiresAt > now) this.profileCache.set(uid, entry);
      });
    } catch {
      // JSON corrupto: limpiamos silenciosamente.
      try { localStorage.removeItem(AuthService.PROFILE_CACHE_KEY); } catch {}
    }
  }

  private getCachedProfile(uid: string): any | null {
    if (this.profileCache.size === 0) this.hydrateProfileCache();
    const entry = this.profileCache.get(uid);
    if (!entry) return null;
    if (Date.now() > entry.expiresAt) {
      this.profileCache.delete(uid);
      this.persistProfileCache();
      return null;
    }
    return entry.profile;
  }

  private setCachedProfile(uid: string, profile: any): void {
    this.profileCache.set(uid, {
      profile,
      expiresAt: Date.now() + AuthService.PROFILE_CACHE_TTL_MS
    });
    this.persistProfileCache();
  }

  // Single source of truth for "which role is authoritative for this user".
  // The JWT (app_metadata.role) is set at login and never lies; the DB
  // (user.role) can drift if the user was updated mid-session, so we treat
  // the JWT as canonical.
  private static effectiveRole(user: { role?: string; app_metadata?: { role?: string } } | null | undefined): string {
    if (!user) return '';
    return user.app_metadata?.role || user.role || '';
  }

  private static isAdminLike(user: { role?: string; app_metadata?: { role?: string } } | null | undefined): boolean {
    const role = AuthService.effectiveRole(user);
    return role === 'superadmin' || role === 'admin';
  }

  // Restore a persisted Supabase session at startup, if any. Never throws:
  // any failure falls through to "no session" so the login screen renders.
  async initialize(): Promise<void> {
    try {
      logger.debug('Inicializando servicio de autenticación...');

      if (localStorage.getItem('crm_google_calendar_auth') === 'true') {
        logger.debug('Detectado OAuth de Google Calendar, omitiendo inicialización de auth...');
        return;
      }

      let session = null;
      try {
        const result = await Promise.race([
          supabase.auth.getSession(),
          new Promise((_, reject) => setTimeout(() => reject(new Error('Session fetch timeout')), 10000))
        ]) as any;
        session = result?.data?.session;

        if (result?.error) {
          logger.warn('Error en getSession, intentando refresh...', result.error.message);
          const { data: refreshData } = await supabase.auth.refreshSession();
          session = refreshData?.session;
        }
      } catch (err: any) {
        logger.warn('getSession timeout, intentando refresh...');
        try {
          const { data: refreshData } = await supabase.auth.refreshSession();
          session = refreshData?.session;
        } catch {
          logger.warn('refreshSession también falló');
        }
      }

      if (!session?.user) {
        logger.info('No hay sesión activa');
        this.currentSession = null;
        sessionIntegrityService.clearSessionFingerprint();
        return;
      }

      logger.info('Sesión existente encontrada para', session.user.email);
      if (session.user.app_metadata?.provider === 'google' && session.provider_token) {
        logger.info('Usuario OAuth Google detectado, omitiendo auth de CRM');
        return;
      }

      try {
        await this.handleAuthenticatedUser(session.user, 'session_reload');
      } catch (profileErr) {
        // Profile sync failed → keep the session alive with basic auth data
        // so the user isn't kicked out on a transient DB blip.
        logger.warn('Error en perfil, usando sesión básica');
        const user = {
          id: session.user.id,
          name: session.user.user_metadata?.full_name || session.user.email?.split('@')[0] || 'Usuario',
          email: session.user.email || '',
          role: session.user.app_metadata?.role || 'user',
          isActive: true,
          createdAt: new Date(),
          app_metadata: session.user.app_metadata || {}
        };
        this.currentSession = { user, permissions: this.getUserPermissions(user) };
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      logger.warn('No se pudo verificar sesión existente:', errorMessage);
      this.currentSession = null;
      sessionIntegrityService.clearSessionFingerprint();
      logger.info('Continuando sin sesión previa (usuario puede hacer login)');
    }
  }

  // Handle authenticated user with robust profile management
  private async handleAuthenticatedUser(
    supabaseUser: any,
    context: 'initial_login' | 'session_reload' | 'token_refresh' | 'auth_event' = 'auth_event'
  ): Promise<void> {
    try {
      logger.debug('Procesando usuario autenticado');
      
      const uid = supabaseUser.id;
      const email = supabaseUser.email;
      
      // Get role from app_metadata (priority) or fallback to 'user'
      const authRole = supabaseUser.app_metadata?.role || 'user';
      logger.debug('Rol desde app_metadata', authRole, '| Contexto:', context);

      // CRITICAL: Validate session integrity (prevent user switching bug)
      const integrityCheck = sessionIntegrityService.validateSessionIntegrity(
        uid,
        email,
        authRole,
        context
      );

      if (!integrityCheck.isValid) {
        logger.error('VIOLACIÓN DE INTEGRIDAD DE SESIÓN', integrityCheck.reason);

        // Log integrity violation (non-blocking)
        const fingerprint = sessionIntegrityService.getSessionFingerprint();
        if (fingerprint) {
          structuredLogger.logIntegrityViolation(
            fingerprint.userId,
            fingerprint.email,
            uid,
            email,
            integrityCheck.reason || 'Unknown violation'
          ).catch(err => logger.warn('Error logging integrity violation:', err));
        }

        if (integrityCheck.severity === 'critical') {
          // In initial_login, a mismatched fingerprint means the previous user
          // logged out without cleanup or a different user is signing in on this
          // browser. Both are legitimate — clear the stale fingerprint and let
          // the new session through. Do NOT signOut, that would kill the login
          // we just succeeded.
          if (context === 'initial_login') {
            logger.warn('Fingerprint stale detectado en login, reemplazando por el del nuevo usuario', integrityCheck.reason);
            sessionIntegrityService.clearSessionFingerprint();
          } else {
            // Not an initial_login → the running session was hijacked or the
            // token belongs to another user. Terminate.
            sessionIntegrityService.clearSessionFingerprint();
            this.currentSession = null;
            await supabase.auth.signOut();

            window.dispatchEvent(new CustomEvent('session-integrity-critical-failure', {
              detail: { reason: integrityCheck.reason, context }
            }));

            throw new Error(`Session integrity violation: ${integrityCheck.reason}`);
          }
        } else {
          // Warning only - log but continue (e.g., legitimate role change)
          logger.warn('Advertencia de integridad (no crítica)', integrityCheck.reason);
        }
      } else if (integrityCheck.severity === 'warning') {
        logger.debug('Session valid with warning', integrityCheck.reason);
      }

      // Generate name from metadata or email
      const name = supabaseUser.user_metadata?.full_name ||
                  supabaseUser.user_metadata?.name ||
                  email.split('@')[0] ||
                  'Usuario';

      // Try to get or create user profile in ONE query using upsert with select
      let profile: any;

      // Check cache first for non-initial logins
      const cached = context !== 'initial_login' ? this.getCachedProfile(uid) : null;
      if (cached) {
        profile = cached;
        logger.debug('Perfil obtenido desde caché');
      } else if (context === 'initial_login') {
        logger.debug('Sincronizando perfil de usuario...');

        try {
          const upsertPromise = supabase
            .from('users')
            .upsert({
              id: uid,
              name: name,
              email: email,
              role: authRole
            }, {
              onConflict: 'id',
              ignoreDuplicates: false
            })
            .select('id, name, email, role, is_active, has_crm_access')
            .single();

          const upsertTimeout = new Promise((_, reject) =>
            setTimeout(() => reject(new Error('Database timeout during profile sync')), 30000)
          );

          const { data: upsertedProfile, error: upsertError } = await Promise.race([
            upsertPromise,
            upsertTimeout
          ]) as any;

          if (upsertError) {
            // Un 400 aquí suele venir del UNIQUE(email) parcial (fila
            // legacy activa con el mismo mail). Intentamos leer el perfil
            // por id primero — si existe, seguimos con esos datos.
            logger.error('Error sincronizando perfil con base de datos:', upsertError?.message);
            try {
              const { data: existingProfile } = await supabase
                .from('users')
                .select('id, name, email, role, is_active, has_crm_access')
                .eq('id', uid)
                .maybeSingle();
              if (existingProfile) {
                profile = existingProfile;
                logger.info('Usando perfil existente (upsert falló pero fila encontrada)');
              } else {
                logger.debug('Sin fila propia, modo degradado con perfil básico');
                profile = { id: uid, name: name, email: email, role: authRole };
              }
            } catch {
              profile = { id: uid, name: name, email: email, role: authRole };
            }
          } else {
            profile = upsertedProfile;
            logger.info('Perfil sincronizado', profile?.name, profile?.email);

            // Block deactivated users at login. We only enforce this when we
            // actually read the flag from DB — degraded/basic profile paths
            // fall through so a Supabase outage doesn't lock everyone out.
            if (profile.is_active === false) {
              sessionIntegrityService.clearSessionFingerprint();
              this.currentSession = null;
              await supabase.auth.signOut();
              throw new Error('Tu cuenta está desactivada. Contacta con un administrador.');
            }

            // Cache the profile
            this.setCachedProfile(uid, profile);
          }
        } catch (error) {
          logger.error('Error crítico en sincronización', error);
          // DEGRADED MODE: Continue with basic profile from auth
          logger.warn('MODO DEGRADADO: Continuando con perfil básico');
          profile = {
            id: uid,
            name: name,
            email: email,
            role: authRole
          };
        }
      } else {
        // For other contexts, just read (with timeout)
        try {
          const readPromise = supabase
            .from('users')
            .select('id, name, email, role, is_active, has_crm_access')
            .eq('id', uid)
            .maybeSingle();

          const readTimeout = new Promise((_, reject) =>
            setTimeout(() => reject(new Error('Database timeout during profile read')), 15000)
          );

          const { data: existingProfile, error } = await Promise.race([
            readPromise,
            readTimeout
          ]) as any;

          if (error) {
            logger.error('Error consultando perfil', error.message);
            // Use cached or basic profile
            profile = {
              id: uid,
              name: name,
              email: email,
              role: authRole
            };
          } else if (!existingProfile) {
            // Profile doesn't exist, use basic profile
            profile = {
              id: uid,
              name: name,
              email: email,
              role: authRole
            };
            logger.debug('Perfil no encontrado, usando perfil básico');
          } else {
            profile = existingProfile;

            // Kick out a session whose user was deactivated after login. Same
            // safety net as initial_login — only enforce when we actually read
            // the flag from DB.
            if (profile.is_active === false) {
              sessionIntegrityService.clearSessionFingerprint();
              this.currentSession = null;
              await supabase.auth.signOut();
              throw new Error('Tu cuenta ha sido desactivada.');
            }

            this.setCachedProfile(uid, profile);
            logger.info('Perfil existente encontrado', profile.name, profile.email);
          }
        } catch (error) {
          logger.error('Timeout leyendo perfil', error);
          // DEGRADED MODE
          profile = {
            id: uid,
            name: name,
            email: email,
            role: authRole
          };
        }

        if (profile.role !== authRole) {
          logger.warn('Role discrepancy detected', { context, dbRole: profile.role, jwtRole: authRole });
        }
      }

      // Determine final role: use DB role during reloads if there's discrepancy
      const finalRole = (context !== 'initial_login' && profile!.role !== authRole)
        ? profile!.role  // Use DB role as authoritative during reloads
        : authRole;      // Use JWT role for initial logins

      // Create user session. isActive falls back to true only when the DB
      // read failed (degraded mode) — an explicit `false` from DB has already
      // thrown above and never reaches here.
      const user: User = {
        id: profile!.id,
        name: profile!.name || 'Usuario',
        email: profile!.email,
        role: finalRole,
        isActive: profile!.is_active !== false,
        createdAt: new Date(),
        app_metadata: supabaseUser.app_metadata || {}
      };

      const permissions = this.getUserPermissions(user);
      this.currentSession = { user, permissions };

      // Cache the profile
      this.setCachedProfile(user.id, profile);

      // Set session fingerprint for future integrity checks
      const appVersion = versionManager.getCurrentVersion();
      sessionIntegrityService.setSessionFingerprint(user.id, user.email, user.role, appVersion);

      // Log the authentication event (non-blocking)
      if (context === 'initial_login') {
        structuredLogger.logLogin(user.id, user.email, user.role).catch(err =>
          logger.warn('Error logging login:', err)
        );
      } else if (context === 'session_reload') {
        structuredLogger.logSessionReload(user.id, user.email, user.role).catch(err =>
          logger.warn('Error logging session reload:', err)
        );
      }

      // Log role sync if there was a discrepancy (non-blocking)
      if (profile!.role !== authRole) {
        structuredLogger.logRoleSync(
          user.id,
          user.email,
          profile!.role,
          authRole,
          context
        ).catch(err => logger.warn('Error logging role sync:', err));
      }

    } catch (error) {
      logger.error('Error handling authenticated user', error);
      // Don't throw - we'll use degraded mode if profile sync fails
      // The session should still be usable with basic auth data
      if (!this.currentSession) {
        throw new Error('Error estableciendo sesión de usuario');
      }
    }
  }

  // Login with email and password with retry logic
  async login(email: string, password: string): Promise<UserSession> {
    const maxRetries = 2;
    let retryCount = 0;

    while (retryCount <= maxRetries) {
      try {
        logger.debug(`Intentando login con: ${email} (intento ${retryCount + 1}/${maxRetries + 1})`);

        // Step 1: Authenticate with Supabase (20 second timeout)
        const loginPromise = supabase.auth.signInWithPassword({
          email: email.trim().toLowerCase(),
          password: password
        });

        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Login timeout')), 20000)
        );

        const { data, error } = await Promise.race([
          loginPromise,
          timeoutPromise
        ]) as any;

        if (error) {
          logger.error('Error de autenticación', error.message);

          // Check for connection errors
          if (error.message?.includes('Failed to fetch') ||
              error.message?.includes('NetworkError') ||
              error.message?.includes('ERR_CONNECTION')) {
            if (retryCount < maxRetries) {
              retryCount++;
              const delayMs = 2000 * retryCount;
              logger.debug(`Error de conexión. Reintentando en ${delayMs}ms...`);
              await new Promise(resolve => setTimeout(resolve, delayMs));
              continue;
            }
            throw new Error('No se pudo conectar con el servidor. Verifica tu conexión a internet.');
          }

          // Handle stale session causing 400 errors on login
          // If we get a non-credentials error (400 Bad Request from stale tokens),
          // clear the local session and retry immediately
          if (retryCount < maxRetries &&
              (error.status === 400 || error.message?.includes('400')) &&
              !error.message?.includes('Invalid login credentials')) {
            logger.warn('Sesión stale detectada, limpiando y reintentando...');
            try {
              await supabase.auth.signOut({ scope: 'local' });
            } catch {
              // Ignore signOut errors
            }
            retryCount++;
            continue;
          }

          throw new Error('Credenciales incorrectas. Verifica tu email y contraseña.');
        }

        if (!data.user) {
          throw new Error('No se pudo obtener información del usuario');
        }

        logger.info('Autenticación exitosa para', data.user.email);

        // Step 2: Handle authenticated user with reasonable timeout (15 seconds).
        // Supabase fires SIGNED_IN right after signInWithPassword resolves; the
        // listener would call handleAuthenticatedUser a second time. Swallow that
        // echo so we don't double-upsert users or double-log the login.
        this.suppressNextSignedIn = true;
        const handleUserPromise = this.handleAuthenticatedUser(data.user, 'initial_login');
        const handleUserTimeout = new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Timeout estableciendo sesión')), 15000)
        );

        try {
          await Promise.race([handleUserPromise, handleUserTimeout]);
        } catch (timeoutError) {
          logger.error('Timeout en handleAuthenticatedUser', timeoutError);

          // If we already have a session (degraded mode), use it
          if (this.currentSession) {
            logger.warn('Usando sesión en modo degradado');
            return this.currentSession;
          }

          // Retry on timeout only if no session was established
          if (retryCount < maxRetries) {
            retryCount++;
            logger.debug(`Reintentando establecer sesión (${retryCount}/${maxRetries})...`);
            await new Promise(resolve => setTimeout(resolve, 1500));
            continue;
          }

          throw new Error('El servidor tardó demasiado en responder. Intenta de nuevo.');
        }

        if (!this.currentSession) {
          throw new Error('Error estableciendo sesión');
        }

        return this.currentSession;

      } catch (error) {
        // Reset the suppression flag so a later real SIGNED_IN is not swallowed
        this.suppressNextSignedIn = false;

        if (retryCount >= maxRetries) {
          logger.error('Error en login después de reintentos', error);
          throw error;
        }

        const errorMessage = error instanceof Error ? error.message : 'Unknown error';

        // Retry on connection or timeout errors
        if (errorMessage.includes('conexión') ||
            errorMessage.includes('connection') ||
            errorMessage.includes('timeout') ||
            errorMessage.includes('Timeout')) {
          retryCount++;
          logger.debug(`Error: ${errorMessage}. Reintentando (${retryCount}/${maxRetries})...`);
          await new Promise(resolve => setTimeout(resolve, 2000 * retryCount));
          continue;
        }

        // For other errors, throw immediately
        throw error;
      }
    }

    throw new Error('No se pudo completar el inicio de sesión');
  }

  // Register new user
  async register(email: string, password: string, name: string): Promise<UserSession> {
    try {
      logger.debug('Registrando nuevo usuario');
      
      const { data, error } = await supabase.auth.signUp({
        email: email.trim().toLowerCase(),
        password: password,
        options: {
          emailRedirectTo: undefined, // Disable email confirmation
          data: {
            full_name: name
          }
        }
      });

      if (error) {
        logger.error('Error de registro', error.message);
        throw error;
      }

      if (!data.user) {
        throw new Error('No se pudo crear el usuario');
      }

      logger.info('Usuario registrado exitosamente', data.user.email);

      // Handle the user immediately (trigger should create profile, but we ensure it)
      await this.handleAuthenticatedUser(data.user, 'initial_login');
      
      if (!this.currentSession) {
        throw new Error('Error estableciendo sesión después del registro');
      }

      return this.currentSession;
    } catch (error) {
      logger.error('Error en registro', error);
      throw error;
    }
  }

  // Reset password
  async resetPassword(email: string): Promise<void> {
    try {
      logger.debug('Enviando reset de contraseña');

      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: window.location.origin
      });

      if (error) {
        logger.error('Error enviando reset', error);
        throw error;
      }

      logger.info('Email de reset enviado');
    } catch (error) {
      logger.error('Error en reset password', error);
      throw error;
    }
  }

  // Update password with recovery token (called from ResetPasswordPage)
  async updatePasswordWithToken(newPassword: string): Promise<void> {
    try {
      logger.debug('Actualizando contraseña con token de recuperación...');

      const { error } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (error) {
        logger.error('Error actualizando contraseña', error);
        throw error;
      }

      logger.info('Contraseña actualizada correctamente');
    } catch (error) {
      logger.error('Error en updatePasswordWithToken', error);
      throw error;
    }
  }

  // Verify a user's password without side-effects on the current session.
  // Supabase has no first-class "verify password" endpoint, so we call
  // signInWithPassword and suppress the SIGNED_IN echo it emits — otherwise
  // the auth listener would treat it as a fresh login and re-run
  // handleAuthenticatedUser, causing duplicate upserts / log entries.
  async verifyCurrentPassword(email: string, password: string): Promise<boolean> {
    this.suppressNextSignedIn = true;
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email: email.trim().toLowerCase(),
        password
      });
      if (error) {
        this.suppressNextSignedIn = false;
        return false;
      }
      return true;
    } catch {
      this.suppressNextSignedIn = false;
      return false;
    }
  }

  // Logout
  async logout(): Promise<void> {
    try {
      // Log logout before clearing session (non-blocking)
      if (this.currentSession?.user) {
        structuredLogger.logLogout(
          this.currentSession.user.id,
          this.currentSession.user.email
        ).catch(err => logger.warn('Error logging logout:', err));
      }

      const { error } = await supabase.auth.signOut();
      if (error) {
        console.error('Error en logout:', error);
      }

      this.currentSession = null;
      sessionIntegrityService.clearSessionFingerprint();
      logger.info('Sesión cerrada correctamente');
    } catch (error) {
      logger.error('Error en logout', error);
      this.currentSession = null;
      sessionIntegrityService.clearSessionFingerprint();
    }
  }

  // Get current session
  getCurrentSession(): UserSession | null {
    return this.currentSession;
  }

  // Check if user is authenticated
  isAuthenticated(): boolean {
    return this.currentSession !== null;
  }

  // Get user permissions based on role.
  // Fine-grained access checks go through rolesService.hasPermission(); this
  // object is a cheap role summary attached to the session for UI gating.
  private getUserPermissions(user: User): UserPermissions {
    const role = AuthService.effectiveRole(user);

    const NONE: UserPermissions = {
      canViewAllContacts: false,
      canEditAllContacts: false,
      canViewAllActivities: false,
      canViewAllMeetings: false,
      canViewAllCompanies: false,
      canManageUsers: false,
      canAccessSettings: false,
      canViewReports: false,
      canManageForms: false
    };

    const ADMIN: UserPermissions = {
      canViewAllContacts: true,
      canEditAllContacts: true,
      canViewAllActivities: true,
      canViewAllMeetings: true,
      canViewAllCompanies: true,
      canManageUsers: true,
      canAccessSettings: true,
      canViewReports: true,
      canManageForms: true
    };

    logger.debug('getUserPermissions', { userId: user.id, role });

    switch (role) {
      case 'superadmin':
      case 'admin':
        return ADMIN;

      case 'colaborador_comercial':
        // Sales collaborator: full CRM read + edit own records
        return {
          ...NONE,
          canViewAllContacts: true,
          canEditAllContacts: true,
          canViewAllActivities: true,
          canViewAllMeetings: true,
          canViewAllCompanies: true,
          canViewReports: true
        };

      case 'colaborador_programador':
        // Dev collaborator: read access to CRM data, no edits
        return {
          ...NONE,
          canViewAllContacts: true,
          canViewAllActivities: true,
          canViewAllMeetings: true,
          canViewAllCompanies: true
        };

      case 'usuario_basico':
      case 'user':
      default:
        return NONE;
    }
  }

  // Listen for auth state changes with improved event handling
  onAuthStateChange(callback: (session: UserSession | null) => void): () => void {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      // Prevent processing duplicate events in quick succession
      const now = Date.now();

      // Generate unique event ID using a rounded timestamp bucket (1 second window)
      const timeBucket = Math.floor(now / 1000);
      const eventId = `${event}_${session?.user?.id}_${timeBucket}`;

      // Check if we already processed this exact event recently
      if (this.processedEventIds.has(eventId)) {
        return;
      }

      // Only block if it's the same event type and user, not all events
      const sameEventKey = `${event}_${session?.user?.id}`;
      const recentSameEvent = this.lastProcessedEventTimestamp > 0 &&
        (now - this.lastProcessedEventTimestamp) < 500;
      if (this.authEventProcessing && recentSameEvent && this.processedEventIds.has(`${sameEventKey}_${Math.floor((now - 500) / 1000)}`)) {
        return;
      }

      logger.debug('Auth state changed', { event });

      // Skip Google Calendar OAuth events in CRM auth
      if (session?.user?.app_metadata?.provider === 'google' && session.provider_token) {
        logger.debug('Evento OAuth Google ignorado en auth de CRM');
        return;
      }

      // Mark event as processed
      this.processedEventIds.add(eventId);

      // Clean up old event IDs (keep only last 5 seconds)
      setTimeout(() => this.processedEventIds.delete(eventId), 5000);

      // Handle sign out
      if (event === 'SIGNED_OUT') {
        this.authEventProcessing = true;
        this.currentSession = null;
        this.profileCache.clear();
        try { localStorage.removeItem(AuthService.PROFILE_CACHE_KEY); } catch {}
        sessionIntegrityService.clearSessionFingerprint();
        callback(null);
        this.authEventProcessing = false;
        this.lastProcessedEventTimestamp = now;
        return;
      }

      const relevantEvents = ['SIGNED_IN', 'TOKEN_REFRESHED', 'USER_UPDATED'];
      if (!relevantEvents.includes(event) || !session?.user) {
        return;
      }

      // Skip the SIGNED_IN echo fired right after an inline login() call
      if (event === 'SIGNED_IN' && this.suppressNextSignedIn) {
        logger.debug('SIGNED_IN post-login inline suprimido');
        this.suppressNextSignedIn = false;
        this.lastProcessedEventTimestamp = now;
        callback(this.currentSession);
        return;
      }

      // For SIGNED_IN events, check if it's from the same user in less than 2 seconds
      if (event === 'SIGNED_IN' && this.currentSession?.user?.id === session.user.id) {
        const timeSinceLastEvent = now - this.lastProcessedEventTimestamp;
        if (timeSinceLastEvent < 2000) {
          logger.debug('SIGNED_IN duplicado para mismo usuario ignorado');
          return;
        }
      }

      // Cross-user login on the same tab (e.g. logout without cleanup + login
      // as someone else) → drop the previous user's cached profile so we don't
      // serve stale data during the new session.
      if (event === 'SIGNED_IN'
          && this.currentSession
          && this.currentSession.user.id !== session.user.id) {
        logger.info('SIGNED_IN de otro usuario, limpiando caché de perfil');
        this.profileCache.clear();
        try { localStorage.removeItem(AuthService.PROFILE_CACHE_KEY); } catch {}
      }

      try {
        this.authEventProcessing = true;
        this.lastProcessedEventTimestamp = now;

        // Determine context based on event type
        // SIGNED_IN on page refresh = session_reload (not a fresh login)
        // SIGNED_IN after explicit login = initial_login (no prior session)
        let context: 'initial_login' | 'session_reload' | 'token_refresh' | 'auth_event';
        if (event === 'SIGNED_IN') {
          context = this.currentSession ? 'session_reload' : 'initial_login';
        } else if (event === 'TOKEN_REFRESHED') {
          context = 'token_refresh';
        } else {
          context = 'auth_event';
        }

        logger.debug('Procesando evento de autenticación', { event, context });
        await this.handleAuthenticatedUser(session.user, context);
        callback(this.currentSession);
      } catch (error) {
        logger.error('Error handling auth state change', error);
        this.currentSession = null;
        sessionIntegrityService.clearSessionFingerprint();
        callback(null);
      } finally {
        this.authEventProcessing = false;
      }
    });

    return () => subscription.unsubscribe();
  }

  // Get current user ID safely
  getCurrentUserId(): string | null {
    return this.currentSession?.user.id || null;
  }

  // Kept for callers already wired to this name. Returns true for both
  // superadmin and admin — the historical semantics — despite the name.
  isSuperAdmin(): boolean {
    return AuthService.isAdminLike(this.currentSession?.user);
  }
}

// Export as default
const authService = new AuthService();
export default authService;